import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileDown, Loader2, CheckCircle2, AlertTriangle,
  ShieldAlert, HelpCircle, PlayCircle, Filter, Building2,
} from 'lucide-react';
import { rolesApi, companiesApi } from '../lib/api';
import { toast } from 'sonner';

const STATE_STYLE = {
  CERTO:       { label: 'Certo',       bg: 'bg-emerald-100', color: 'text-emerald-800', icon: CheckCircle2 },
  PROBABILE:   { label: 'Probabile',   bg: 'bg-amber-100',   color: 'text-amber-900',   icon: AlertTriangle },
  AMBIGUO:     { label: 'Ambiguo',     bg: 'bg-orange-100',  color: 'text-orange-900',  icon: ShieldAlert },
  NON_TROVATO: { label: 'Non trovato', bg: 'bg-slate-100',   color: 'text-slate-700',   icon: HelpCircle },
};

const StateBadge = ({ state }) => {
  const s = STATE_STYLE[state] || STATE_STYLE.NON_TROVATO;
  const Ic = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.color}`}>
      <Ic size={10} /> {s.label}
    </span>
  );
};

const RolesImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {token, counters, rows, filename, total_rows}
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filterState, setFilterState] = useState('ALL');
  const [mode, setMode] = useState('union'); // union | replace
  const [applying, setApplying] = useState(false);
  const [applyState, setApplyState] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(''); // opzionale: se impostato scopa il match all'azienda
  const [companySearch, setCompanySearch] = useState('');

  useEffect(() => {
    rolesApi.catalog().then((r) => setRolesCatalog(r.data || [])).catch(() => {});
    companiesApi.list().then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const filteredCompanies = useMemo(() => {
    const s = companySearch.trim().toLowerCase();
    if (!s) return companies.slice(0, 200);
    return companies.filter((c) => (c.name || '').toLowerCase().includes(s)).slice(0, 200);
  }, [companies, companySearch]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyId) || null,
    [companies, companyId],
  );

  const labelOfRole = (key) => (rolesCatalog.find((x) => x.roleKey === key)?.roleLabel) || key;

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    setSelectedRows(new Set());
    setApplyState(null);
  };

  const uploadPreview = async () => {
    if (!file) { toast.error('Seleziona prima un file Excel'); return; }
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (companyId) fd.append('companyId', companyId);
      const { data } = await rolesApi.previewImport(fd);
      setPreview(data);
      // Default: include CERTO + PROBABILE (righe con resolved_roles)
      const initial = new Set();
      (data.rows || []).forEach((r) => {
        if (
          (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') &&
          (r.resolved_roles || []).length > 0
        ) {
          initial.add(r.row_number);
        }
      });
      setSelectedRows(initial);
      const cn = data.companyName ? ` · scope ${data.companyName}` : '';
      toast.success(`Preview${cn}: ${data.counters.CERTO} CERTI · ${data.counters.PROBABILE} PROBABILI · ${data.counters.AMBIGUO} AMBIGUI · ${data.counters.NON_TROVATO} NON TROVATI`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
    } finally {
      setUploading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    if (filterState === 'ALL') return preview.rows;
    return preview.rows.filter((r) => r.match_state === filterState);
  }, [preview, filterState]);

  const toggleRow = (rn) => {
    setSelectedRows((prev) => {
      const nx = new Set(prev);
      if (nx.has(rn)) nx.delete(rn); else nx.add(rn);
      return nx;
    });
  };

  const toggleAllVisible = () => {
    if (!preview) return;
    const eligible = filteredRows.filter((r) =>
      (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') &&
      (r.resolved_roles || []).length > 0,
    );
    const allSelected = eligible.every((r) => selectedRows.has(r.row_number));
    setSelectedRows((prev) => {
      const nx = new Set(prev);
      if (allSelected) eligible.forEach((r) => nx.delete(r.row_number));
      else eligible.forEach((r) => nx.add(r.row_number));
      return nx;
    });
  };

  const doApply = async () => {
    if (!preview) return;
    if (selectedRows.size === 0) { toast.error('Nessuna riga selezionata'); return; }
    if (!window.confirm(`Applicare i ruoli su ${selectedRows.size} lavoratori?\n\nModalità: ${mode === 'union' ? 'UNIONE (mantiene ruoli esistenti)' : 'SOSTITUZIONE (sovrascrive ruoli esistenti)'}\n\nBackup automatico prima della scrittura.`)) return;
    setApplying(true);
    setApplyState({ state: 'starting', phase: 'starting', progress: 0, total: selectedRows.size });
    try {
      const { data } = await rolesApi.applyImport({
        token: preview.token,
        mode,
        row_numbers: Array.from(selectedRows),
      });
      const opId = data.opId;
      const poll = setInterval(async () => {
        try {
          const { data: st } = await rolesApi.applyStatus(opId);
          setApplyState(st);
          if (st.state === 'done') {
            clearInterval(poll);
            toast.success(`Ruoli assegnati a ${st.total_applied} lavoratori · opId ${opId}`);
            setApplying(false);
          } else if (st.state === 'error') {
            clearInterval(poll);
            toast.error(`Errore: ${st.error}`);
            setApplying(false);
          }
        } catch (err) {
          clearInterval(poll);
          setApplying(false);
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setApplying(false);
    }
  };

  const counters = preview?.counters || {};
  const total = preview?.total_rows || 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-900"
            data-testid="roles-import-back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#023F80]">Import Ruoli Excel</h1>
            <p className="text-sm text-slate-500">Assegna ruoli a più lavoratori tramite file Excel · match CERTO / PROBABILE / AMBIGUO / NON TROVATO</p>
          </div>
        </div>
        <button
          onClick={() => rolesApi.downloadTemplate()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50"
          data-testid="roles-import-download-template"
        >
          <FileDown size={14} /> Scarica template
        </button>
      </div>

      {/* Uploader */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-1">1. Carica il file Excel</p>
            <p className="text-xs text-slate-500">
              Colonne accettate (flessibili): <code>Cognome</code>+<code>Nome</code> oppure una sola colonna
              <code> Dipendente</code>/<code>Nominativo</code>/<code>Lavoratore</code>.
              Il <code>CodiceFiscale</code> è <em>opzionale</em>: se assente si prova il match su nome+cognome.
              <br />
              La colonna <code>Ruoli</code> può contenere più ruoli separati da <code>;</code>/<code>,</code>
              &nbsp;oppure <strong>concatenati senza separatore</strong> (es. "LAVORATORE PRIMO SOCCORSO"),
              e vengono riconosciuti automaticamente.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={14} className="text-slate-500" />
              <p className="text-xs font-semibold text-slate-700">
                Azienda predefinita <span className="text-slate-400 font-normal">(opzionale — se conosci l'azienda del file)</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                placeholder="Cerca azienda…"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                data-testid="roles-import-company-search"
                className="text-xs px-2 py-1.5 border border-slate-200 rounded-md flex-1 min-w-[220px]"
              />
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                data-testid="roles-import-company-select"
                className="text-xs px-2 py-1.5 border border-slate-200 rounded-md bg-white flex-1 min-w-[260px]"
              >
                <option value="">— Nessuna: match globale (CF prioritario) —</option>
                {filteredCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
                {companies.length > 200 && !companySearch && (
                  <option disabled>… ({companies.length} aziende totali, cerca per restringere)</option>
                )}
              </select>
              {selectedCompany && (
                <button
                  onClick={() => setCompanyId('')}
                  className="text-[10px] text-red-600 hover:underline"
                  data-testid="roles-import-company-clear"
                >
                  ✕ togli
                </button>
              )}
            </div>
            {selectedCompany && (
              <p className="text-[10px] text-emerald-700 mt-1 font-semibold">
                ✓ Match limitato ai lavoratori di "{selectedCompany.name}" → match su solo Cognome+Nome più affidabile (CERTO invece di PROBABILE).
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".xlsx,.xlsm,.xlsb"
              onChange={onFileChange}
              data-testid="roles-import-file-input"
              className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            <button
              onClick={uploadPreview}
              disabled={!file || uploading}
              data-testid="roles-import-preview-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Analizzo…' : 'Anteprima'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <p className="text-sm font-semibold text-slate-700">
              2. Anteprima · <span className="text-slate-500 font-normal">{preview.filename} · {total} righe</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {['CERTO', 'PROBABILE', 'AMBIGUO', 'NON_TROVATO'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterState(filterState === s ? 'ALL' : s)}
                  data-testid={`roles-import-filter-${s}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    filterState === s ? 'border-[#023F80] bg-indigo-50 text-[#023F80] font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {STATE_STYLE[s].label} · {counters[s] || 0}
                </button>
              ))}
              <button
                onClick={() => setFilterState('ALL')}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  filterState === 'ALL' ? 'border-[#023F80] bg-indigo-50 text-[#023F80] font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="roles-import-filter-ALL"
              >
                Tutte · {total}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500 font-semibold">Modalità:</span>
            <label className="inline-flex items-center gap-1 text-xs text-slate-700 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="union"
                checked={mode === 'union'}
                onChange={() => setMode('union')}
                data-testid="roles-import-mode-union"
              />
              Aggiungi ai ruoli esistenti (union)
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-slate-700 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="replace"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
                data-testid="roles-import-mode-replace"
              />
              Sostituisci ruoli esistenti (replace)
            </label>
            <div className="flex-1" />
            <span className="text-xs text-slate-500">
              Selezionati: <strong>{selectedRows.size}</strong>
            </span>
            <button
              onClick={doApply}
              disabled={applying || selectedRows.size === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white disabled:opacity-60"
              data-testid="roles-import-apply-btn"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
              {applying ? 'Applico…' : 'Applica ruoli'}
            </button>
          </div>

          {applyState && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-700">Stato apply:</span>
                <span className="text-slate-600">{applyState.phase || applyState.state}</span>
                {applyState.state === 'done' && <CheckCircle2 size={14} className="text-emerald-600" />}
              </div>
              {applyState.total > 0 && (
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-emerald-500 transition-all"
                    style={{ width: `${Math.round(((applyState.progress || 0) / applyState.total) * 100)}%` }}
                  />
                </div>
              )}
              <p className="text-slate-500 mt-1">
                {applyState.progress || 0} / {applyState.total || 0}
                {applyState.state === 'done' && ` · ${applyState.total_applied || 0} lavoratori aggiornati`}
              </p>
            </div>
          )}

          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-slate-100 rounded-xl">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 text-left">
                    <button
                      onClick={toggleAllVisible}
                      className="text-[10px] font-semibold text-[#023F80] hover:underline"
                      data-testid="roles-import-toggle-all"
                    >
                      Sel.
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left">Riga</th>
                  <th className="px-3 py-2 text-left">Stato</th>
                  <th className="px-3 py-2 text-left">Input Excel</th>
                  <th className="px-3 py-2 text-left">Match</th>
                  <th className="px-3 py-2 text-left">Ruoli da assegnare</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const isSel = selectedRows.has(r.row_number);
                  const canSelect = (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') && (r.resolved_roles || []).length > 0;
                  return (
                    <tr
                      key={r.row_number}
                      className={`border-b border-slate-50 ${isSel ? 'bg-indigo-50/50' : ''} hover:bg-slate-50/60`}
                      data-testid={`roles-import-row-${r.row_number}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          disabled={!canSelect}
                          onChange={() => toggleRow(r.row_number)}
                          data-testid={`roles-import-check-${r.row_number}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.row_number}</td>
                      <td className="px-3 py-2"><StateBadge state={r.match_state} /></td>
                      <td className="px-3 py-2 text-slate-700">
                        <div className="font-semibold">
                          {r.input.lastName} {r.input.firstName}
                        </div>
                        <div className="font-mono text-[10px] text-slate-400">
                          {r.input.fiscalCode || 'CF assente'}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          Ruoli Excel: {r.input.ruoli_raw.join(' · ') || '—'}
                        </div>
                        {r.unknown_roles?.length > 0 && (
                          <div className="text-[10px] text-orange-600 mt-0.5">
                            ⚠ Ruoli non riconosciuti: {r.unknown_roles.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {r.worker ? (
                          <div>
                            <div className="font-semibold">{r.worker.lastName} {r.worker.firstName}</div>
                            <div className="text-[10px] text-slate-500">{r.worker.companyName}</div>
                            {r.worker.current_ruoli?.length > 0 && (
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Attuali: {r.worker.current_ruoli.map(labelOfRole).join(', ')}
                              </div>
                            )}
                          </div>
                        ) : r.candidates?.length > 0 ? (
                          <div>
                            <div className="text-orange-700 font-semibold">{r.candidates.length} candidati:</div>
                            {r.candidates.slice(0, 3).map((c) => (
                              <div key={c.id} className="text-[10px] text-slate-500">
                                · {c.lastName} {c.firstName} — {c.companyName}
                              </div>
                            ))}
                            {r.candidates.length > 3 && (
                              <div className="text-[10px] text-slate-400">…+{r.candidates.length - 3}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        <div className="text-[10px] text-slate-400 mt-0.5">{r.match_reason}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(r.resolved_roles || []).map((k) => (
                            <span
                              key={k}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-100"
                            >
                              {labelOfRole(k)}
                            </span>
                          ))}
                          {(r.resolved_roles || []).length === 0 && (
                            <span className="text-[10px] text-slate-400">nessuno</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-6 text-xs text-slate-400">
                      Nessuna riga con questo filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesImportPage;
